"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DimensionedPeriodizer = void 0;
const tslib_1 = require("tslib");
const sharedFunctions_1 = require("../sharedFunctions");
const enumerableDecorator_1 = require("../../enumerableDecorator");
const ModelCommon_1 = require("../../../ModelCommon");
const util_1 = require("../../../../util");
const debug = util_1.DEBUG_OVERRIDE || (util_1.NOT_LAMBDA && false);
const className = 'DimensionedPeriodizer';
class DimensionedPeriodizer extends ModelCommon_1.ModelCommon {
    constructor(ob) {
        super(ob?.type ?? className, (({ sk, pk, type, id, name }) => ({ sk, pk, type, id, name }))(ob ?? {}));
        this.initialize = () => {
            const xFn = () => ({});
            const periodized = this.arrayLengths ? (0, util_1.fillWithXByArrayDef)(this.arrayLengths, xFn)() : undefined;
            periodized && this.ingest({ periodized, arrayLengths: this.arrayLengths, lastUpdatedTimestamp: undefined });
        };
        this.replaceTopLevelEntry = (periodizerIdx, xFn = () => ({})) => {
            const emptyMapByArrayDef = (arrDef) => arrDef.reduceRight((a, c, i) => {
                const map = {};
                for (let i = 0; i < c; i++) {
                    map[i] = { ...a };
                }
                return map;
            }, xFn());
            const [first, ...remaining] = this.arrayLengths ?? [];
            const newRow = emptyMapByArrayDef(remaining);
            this.periodized && (this.periodized[periodizerIdx] = newRow);
            throw new Error('not usable right now as need to deep clone accumulator or it will be same object in multiple places ');
        };
        this.getDimensions = () => {
            let dimStr = [];
            let map = this.periodized;
            do {
                const keys = map && Object.keys(map);
                keys && keys.length > 0 && keys.every((k) => typeof parseInt(k) === 'number') && dimStr.push(keys.length);
                map = map?.[0];
            } while (map);
            return dimStr;
        };
        this.inferDimensions = (a) => {
            const inferredArrayLength = [];
            let ax = a;
            do {
                ax && inferredArrayLength.push(ax.length);
                ax = ax[0];
            } while (ax instanceof Array);
            return inferredArrayLength;
        };
        this.ingest = ({ periodized: deydratedPeriodized, keyMap, lastUpdatedTimestamp, sk, pk, type, id, name }) => {
            const rehydrateLevel = (levelArrMap) => {
                if (levelArrMap instanceof Array) {
                    const arr = levelArrMap;
                    return Object.fromEntries(Object.entries(arr).map(([i, nextLevelArrMap]) => [i, rehydrateLevel(nextLevelArrMap)]));
                }
                else {
                    const map = this.decodeMap(levelArrMap);
                    return map;
                }
            };
            this.keyMap = keyMap;
            this.arrayLengths = deydratedPeriodized && this.inferDimensions(deydratedPeriodized);
            this.periodized = deydratedPeriodized ? rehydrateLevel(deydratedPeriodized) : undefined;
            this.lastUpdatedTimestamp = lastUpdatedTimestamp;
            sk && (this.sk = sk);
            pk && (this.pk = pk);
            type && (this.type = type);
            id && (this.id = id);
            name && (this.name = name);
        };
        this.egest = () => this.periodized ? { ...this.dehydrate(), keyMap: this.keyMap, type: this.type, pk: this.pk, sk: this.sk, id: this.id, name: this.name } : undefined;
        this.getShortKey = (k) => {
            let shortCMVKey = k;
            if (!this.keyMap) {
                this.keyMap = {};
            }
            if (this.keyMap?.[k]) {
                shortCMVKey = this.keyMap[k];
            }
            else {
                const keyMapEntriesNum = Object.keys(this.keyMap).length;
                shortCMVKey = String.fromCharCode(keyMapEntriesNum + 65);
                this.keyMap[k] = shortCMVKey;
            }
            return shortCMVKey;
        };
        this.getOriginalKey = (shortKey) => {
            let originalCMVKey = shortKey;
            if (this.keyMap) {
                const matchedEntry = Object.entries(this.keyMap).find(([k, v]) => v === shortKey);
                if (matchedEntry) {
                    const [ok, v] = matchedEntry;
                    originalCMVKey = ok;
                }
            }
            return originalCMVKey;
        };
        this.encodeMap = (map) => Object.fromEntries(Object.entries(map).map(([k, v]) => [this.getShortKey(k), v]));
        this.decodeMap = (map) => Object.fromEntries(Object.entries(map).map(([k, v]) => [this.getOriginalKey(k), v]));
        this.dehydrate = () => {
            const dehydateLayer = (mapish) => {
                const keys = Object.keys(mapish);
                const isArrayLike = keys.length > 0 && keys.every((k) => !isNaN(parseInt(k)));
                if (isArrayLike) {
                    const arrayish = mapish;
                    return Object.values(arrayish).map((nextMapish) => dehydateLayer(nextMapish));
                }
                else {
                    //debug && console.log('dehydratiing end map', mapish)
                    return this.encodeMap(mapish);
                }
            };
            const dehydated = this.periodized ? dehydateLayer(this.periodized) : undefined;
            return { periodized: dehydated, keyMap: this.keyMap, arrayLengths: this.arrayLengths, lastUpdatedTimestamp: this.lastUpdatedTimestamp };
        };
        this.getByIndex = (...args) => args.reduce((a, c) => {
            const accessed = a && typeof a === 'object' ? a[c] : a;
            return accessed;
        }, this.periodized);
        this.setKeyByIndex = (mapKey, mapVal, ...args) => {
            if (this.getByIndex(...args)) {
                const sector = this.getByIndex(...args);
                if (sector) {
                    debug && console.log(`setting sector ${args} with val ${sector[mapKey]} to: ${mapVal}`);
                    sector[mapKey] = mapVal;
                }
            }
        };
        this.accumulateKeyByIndex = (mapKey, mapVal, ...args) => {
            if (this.getByIndex(...args)) {
                const sector = this.getByIndex(...args);
                if (sector && !sector[mapKey]) {
                    sector[mapKey] = 0;
                }
                if (mapVal !== null && sector?.[mapKey] !== undefined && (sector[mapKey] === null || typeof sector[mapKey] === 'number')) {
                    const currentVal = sector[mapKey] ?? 0;
                    debug && console.log(`accumulating sector ${args} with val ${sector[mapKey]} by ${mapVal} to ${mapVal + currentVal}`);
                    sector[mapKey] = mapVal + currentVal;
                }
                else {
                    debug && console.log(`not accumulating because`, { mapKey, mapVal, sectorIsNull: !sector, sectorKeyIsNull: !sector[mapKey] });
                }
            }
        };
        this.setMapByIndex = (accessor, update) => {
            if (this.getByIndex(...accessor)) {
                const sector = this.getByIndex(...accessor);
                if (sector) {
                    Object.keys(sector).forEach((key) => delete sector[key]);
                    debug && console.log(`setting sector ${accessor} with vals ${Object.values(sector).join('')} to ${Object.values(update).join('')}}`);
                    Object.assign(sector, update);
                }
            }
        };
        this.timestampToAccessor = (timestamp) => this.timeBucketToAccessor((0, util_1.timestampToYMDHMS)(timestamp));
        this.setMapByTimestamp = (map, timestamp) => this.setMapByIndex(this.timestampToAccessor(timestamp), map);
        this.setKeyByTimestamp = (k, v, timestamp) => this.setKeyByIndex(k, v, ...this.timestampToAccessor(timestamp));
        this.accumulateKeyByTimestamp = (k, v, timestamp) => this.accumulateKeyByIndex(k, v, ...this.timestampToAccessor(timestamp));
        this.getByTimestamp = (timestamp) => this.getByIndex(...this.timestampToAccessor(timestamp));
        /**
         * @function nullizeOverwritebles finds buckets exclusively between the last updated bucket, plus the to-be updated bucket if not the same as the last updatedBucket and makes contents of contained map null, so that count is effectively restarted on rollover
         * @param timestamp
         */
        this.nullizeOverwritebles = (timestamp) => {
            if (this.lastUpdatedTimestamp && this.arrayLengths) {
                const initialAccessor = this.timestampToAccessor(this.lastUpdatedTimestamp);
                const finalAccessor = this.timestampToAccessor(timestamp);
                if (!finalAccessor.every((v, i) => v === initialAccessor[i])) {
                    const incrementBy = 1;
                    const arrayLengths = this.arrayLengths;
                    const nullableAccessors = [...(0, sharedFunctions_1.getAccessorsBetween)({ initialAccessor, arrayLengths, incrementBy, finalAccessor }), finalAccessor];
                    debug && console.log(`Nullifying ${nullableAccessors.length} becuase `, { initialAccessor, finalAccessor });
                    nullableAccessors.forEach((accessor) => {
                        const map = this.getByIndex(...accessor);
                        const clone = { ...map };
                        if (map && typeof map === 'object') {
                            const keys = Object.keys(map);
                            keys.forEach((k) => (map[k] = null));
                            debug && console.log(`nullifiying`, { accessor, clone, map });
                            this.setMapByIndex(accessor, map);
                        }
                    });
                }
            }
        };
        this.accumulateByTimeBucket = (measureValueMapRange) => {
            const apportionmentRes = (0, util_1.apportionMeasureValuesMap)({ timeBucketDef: this.timeBucketDefinition, measureValueMapRange });
            debug && console.log(apportionmentRes);
            debug &&
                console.log('apportionmentRes total', apportionmentRes.reduce((a, c) => (a += c.apportionedValuesMap.cantering ?? 0), 0));
            apportionmentRes.forEach((apportionment) => {
                const valueIncrementMap = apportionment.apportionedValuesMap;
                const bucketStart = apportionment.bucket[0];
                this.nullizeOverwritebles(bucketStart);
                Object.entries(valueIncrementMap).forEach(([k, v]) => this.accumulateKeyByTimestamp(k, v, bucketStart));
                this.lastUpdatedTimestamp = bucketStart;
            });
        };
        this.toStateSnapShotsByAccessor = (initialAccessor, finalAccessor) => {
            const stateSnapShots = [];
            if (this.arrayLengths) {
                const accessors = [
                    initialAccessor,
                    ...(0, sharedFunctions_1.getAccessorsBetween)({ initialAccessor, finalAccessor, arrayLengths: this.arrayLengths, incrementBy: 1 }),
                    finalAccessor,
                ];
                if (initialAccessor.every((v, i) => v === finalAccessor[i])) {
                    accessors.shift();
                }
                accessors.filter(this.isValidAccessor).forEach((accessor) => {
                    const state = this.getByIndex(...accessor);
                    if (state && typeof state === 'object') {
                        const stateSnapShot = {
                            timestamp: this.accessorToTimestamp(accessor),
                            state,
                        };
                        stateSnapShots.push(stateSnapShot);
                    }
                });
            }
            return stateSnapShots;
        };
        this.toSnapShotByTimestampRange = ([startTimestamp, endTimeStamp]) => {
            // TODO if ouside this.latest - this number of days throw error
            const initialAccessor = this.timestampToAccessor(startTimestamp);
            const finalAccessor = this.timestampToAccessor(endTimeStamp);
            return this.toStateSnapShotsByAccessor(initialAccessor, finalAccessor);
        };
        if (ob) {
            if (ob.periodized instanceof Array) {
                this.ingest(ob);
            }
            else {
                this.periodized = ob.periodized;
                this.arrayLengths = ob.arrayLengths;
                this.keyMap = ob.keyMap;
                this.lastUpdatedTimestamp = ob.lastUpdatedTimestamp;
            }
        }
    }
}
exports.DimensionedPeriodizer = DimensionedPeriodizer;
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], DimensionedPeriodizer.prototype, "initialize", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], DimensionedPeriodizer.prototype, "replaceTopLevelEntry", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], DimensionedPeriodizer.prototype, "getDimensions", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], DimensionedPeriodizer.prototype, "inferDimensions", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], DimensionedPeriodizer.prototype, "ingest", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], DimensionedPeriodizer.prototype, "egest", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], DimensionedPeriodizer.prototype, "getShortKey", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], DimensionedPeriodizer.prototype, "getOriginalKey", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], DimensionedPeriodizer.prototype, "encodeMap", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], DimensionedPeriodizer.prototype, "decodeMap", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], DimensionedPeriodizer.prototype, "dehydrate", void 0);
